import { useState, useRef, useEffect } from 'react'
import styles from './styles.module.css'
import { IOrderNote } from '../../../../../app/entities/OrderNote'
import FilterListIcon from '@mui/icons-material/FilterList'
import PrimaryCheckbox from '../checkbox/Checkbox'
import { NoteType } from '../../../../../app/types/order-notes'

export interface OnsiteCancellationNote {
  cosReason1?: string
  cosReason2?: string
  cosReason3?: string
  cosNote?: string
}

interface OrderNotesTableProps {
  notes: IOrderNote[]
  isLoading?: boolean
  maxHeight?: number
  height?: number
  title?: string
  onsiteCancellation?: OnsiteCancellationNote | null
}

// Filter types that can be toggled - note types plus onsite_cancellation for display
type FilterType = NoteType | 'onsite_cancellation'

function formatDate(date: Date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function sortNotes(notes: IOrderNote[]): IOrderNote[] {
  const typeOrder: Record<NoteType, number> = {
    technician_note: 0,
    legacy_note: 1,
    activity_log: 2,
  }

  return [...notes].sort((a, b) => {
    const typeA = typeOrder[a.type] ?? 3
    const typeB = typeOrder[b.type] ?? 3

    if (typeA !== typeB) {
      return typeA - typeB
    }

    // Handle null created dates (legacy notes without dates go to the end of their type group)
    const timeA = a.created ? new Date(a.created).getTime() : 0
    const timeB = b.created ? new Date(b.created).getTime() : 0
    return timeB - timeA
  })
}

function buildOnsiteCancellationContent(cos: OnsiteCancellationNote): string {
  const parts: string[] = []

  if (cos.cosReason1) parts.push(`Reason: ${cos.cosReason1}`)
  if (cos.cosReason2) parts.push(`Secondary: ${cos.cosReason2}`)
  if (cos.cosReason3) parts.push(`Additional: ${cos.cosReason3}`)
  if (cos.cosNote) parts.push(`Note: ${cos.cosNote}`)

  return parts.join(' | ')
}

function NoteRow({ note }: { note: IOrderNote }) {
  const hasCreatedDate = note.created !== null
  const hasAuthor = Boolean(note.createdBy)

  return (
    <div className={styles.noteRow}>
      <div className={styles.noteMeta}>
        {hasAuthor && (
          <span className={styles.noteAuthor}>{note.createdBy}</span>
        )}
        {hasAuthor && hasCreatedDate && (
          <span className={styles.metaSeparator}>•</span>
        )}
        {hasCreatedDate && (
          <span className={styles.noteDate}>
            {formatDate(new Date(note.created))}
          </span>
        )}
        {!hasAuthor && !hasCreatedDate && (
          <span className={styles.noteDate}>Legacy Note</span>
        )}
      </div>
      <div className={styles.noteContent}>{note.content.trimStart()}</div>
    </div>
  )
}

function OnsiteCancellationRow({ data }: { data: OnsiteCancellationNote }) {
  return (
    <div className={`${styles.noteRow} ${styles.onsiteCancellationRow}`}>
      <div className={styles.noteMeta}>
        <span className={styles.onsiteCancellationLabel}>
          On-site Cancellation
        </span>
        <span className={styles.noteAuthor}>Technician</span>
      </div>
      <div className={styles.noteContent}>
        {buildOnsiteCancellationContent(data)}
      </div>
    </div>
  )
}

export default function OrderNotesTable({
  notes,
  isLoading = false,
  maxHeight,
  height,
  title = 'Work Order Notes',
  onsiteCancellation,
}: OrderNotesTableProps) {
  const hasOnsiteCancellation = Boolean(
    onsiteCancellation &&
      (onsiteCancellation.cosReason1 ||
        onsiteCancellation.cosNote ||
        onsiteCancellation.cosReason2 ||
        onsiteCancellation.cosReason3),
  )

  const [filters, setFilters] = useState<Record<FilterType, boolean>>({
    technician_note: true,
    activity_log: true,
    legacy_note: true,
    onsite_cancellation: true,
  })
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowFilterDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleFilterChange(type: FilterType) {
    setFilters((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
  }

  const filteredNotes = notes.filter((note) => {
    const noteType = note.type as NoteType
    return filters[noteType] ?? true
  })

  const sortedNotes = sortNotes(filteredNotes)

  const showOnsiteCancellation =
    hasOnsiteCancellation && filters.onsite_cancellation

  const activeFilterCount = Object.values(filters).filter((v) => !v).length

  const hasAnyContent = sortedNotes.length > 0 || showOnsiteCancellation
  const totalItemCount = notes.length + (hasOnsiteCancellation ? 1 : 0)

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div
          className={styles.filterContainer}
          ref={dropdownRef}
        >
          <button
            type='button'
            className={styles.filterButton}
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <FilterListIcon fontSize='small' />
            {activeFilterCount > 0 && (
              <span className={styles.filterBadge}>{activeFilterCount}</span>
            )}
          </button>
          {showFilterDropdown && (
            <div className={styles.filterDropdown}>
              <PrimaryCheckbox
                checked={filters.technician_note}
                label='Technician Notes'
                onChange={() => handleFilterChange('technician_note')}
              />
              <PrimaryCheckbox
                checked={filters.activity_log}
                label='Activity Logs'
                onChange={() => handleFilterChange('activity_log')}
              />
              {hasOnsiteCancellation && (
                <PrimaryCheckbox
                  checked={filters.onsite_cancellation}
                  label='On-site Cancellation'
                  onChange={() => handleFilterChange('onsite_cancellation')}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={styles.container}
        style={{ maxHeight, height }}
      >
        {isLoading ? (
          <div className={styles.loadingMessage}>Loading notes...</div>
        ) : !hasAnyContent ? (
          <div className={styles.emptyMessage}>
            {totalItemCount === 0 ? 'No notes yet.' : 'No notes match filters.'}
          </div>
        ) : (
          <>
            {showOnsiteCancellation && onsiteCancellation && (
              <OnsiteCancellationRow data={onsiteCancellation} />
            )}
            {sortedNotes.map((note) => (
              <NoteRow
                key={note.objectId}
                note={note}
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}
